import { useCallback, useEffect, useRef, useState } from 'react'
import { Autocomplete, GoogleMap, InfoWindowF } from '@react-google-maps/api'
import { Compass, Loader2, MapPin, Navigation } from 'lucide-react'
import { adminApi } from '../lib/api'
import type { RecommendedContentItem } from '../types'
import { useGoogleMaps } from '../components/maps/GoogleMapsProvider'
import { toNumericCoordinate } from '../utils/location'

type ExploreItemType = 'trip' | 'event' | 'post'
type ContentFilter = 'all' | 'trip' | 'post' | 'event'

interface ExploreItem {
  id: string
  sourceId: string
  type: ExploreItemType
  title: string
  subtitle?: string
  description?: string
  coverImage?: string
  location: {
    lat: number
    lng: number
    address?: string
  }
  metadata?: {
    status?: string
    startDate?: number | string | null
    endDate?: number | string | null
    distance?: number
    popularityScore?: number
    stats?: {
      likes?: number
      comments?: number
      participantCount?: number
      favoritesCount?: number
    }
  }
}

const defaultCenter = { lat: 40.7128, lng: -74.006 } // NYC fallback
const defaultZoom = 3

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  mapId: 'WAYBOOK_EXPLORE_MAP', // Required for AdvancedMarkerElement
}

const parseRecommendedItems = (items: RecommendedContentItem[]): ExploreItem[] => {
  return items
    .map<ExploreItem | null>((item) => {
      // Extract location based on content type
      let lat: number | null = null
      let lng: number | null = null
      let address: string | undefined

      if (item.location) {
        lat = toNumericCoordinate(item.location.latitude)
        lng = toNumericCoordinate(item.location.longitude)
        address = item.location.name
      }

      // Skip items without valid location
      if (lat === null || lng === null) return null

      // Determine title based on content type
      let title = ''
      if (item.type === 'trip') {
        title = item.title || 'Untitled Trip'
      } else if (item.type === 'event') {
        title = item.eventTitle || item.title || 'Untitled Event'
      } else if (item.type === 'post') {
        title = item.content?.substring(0, 50) || 'Post'
        if (item.content && item.content.length > 50) title += '...'
      }

      return {
        id: `${item.type}-${item.id}`,
        sourceId: item.id,
        type: item.type,
        title,
        subtitle: item.creator?.name,
        description: item.description || item.content,
        coverImage: item.coverImage || item.photo?.url,
        location: {
          lat,
          lng,
          address,
        },
        metadata: {
          status: item.status,
          startDate: item.startDate || item.eventStartDate || null,
          endDate: item.endDate || item.eventEndDate || null,
          distance: item.distance,
          popularityScore: item.popularityScore,
          stats: item.stats,
        },
      }
    })
    .filter((item): item is ExploreItem => Boolean(item))
}

const formatDate = (value?: number | string | null) => {
  if (!value) return undefined
  const date =
    typeof value === 'number'
      ? new Date(value > 100000000000 ? value : value * 1000)
      : new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleDateString()
}

export default function ExplorePage() {
  const { isLoaded, loadError } = useGoogleMaps()
  const mapRef = useRef<google.maps.Map | null>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const markersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map())

  const [searchValue, setSearchValue] = useState('')
  const [geolocating, setGeolocating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [allItems, setAllItems] = useState<ExploreItem[]>([])
  const [visibleItems, setVisibleItems] = useState<ExploreItem[]>([])
  const [selectedItem, setSelectedItem] = useState<ExploreItem | null>(null)
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [mapZoom, setMapZoom] = useState(defaultZoom)
  const [contentFilter, setContentFilter] = useState<ContentFilter>('all')
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)

  const loadData = useCallback(async (latitude?: number, longitude?: number) => {
    setLoading(true)
    try {
      const response = await adminApi.getRecommendedContent({
        limit: 50,
        offset: 0,
        contentType: contentFilter,
        latitude,
        longitude,
        radius: 100,
      })

      const data = response.data
      let items: ExploreItem[] = []

      if (data && typeof data === 'object' && 'data' in data) {
        items = parseRecommendedItems(data.data || [])
      }

      setAllItems(items)
      setVisibleItems(items)
    } catch (error) {
      console.error('Failed to load explore data:', error)
      setAllItems([])
      setVisibleItems([])
    } finally {
      setLoading(false)
    }
  }, [contentFilter])

  useEffect(() => {
    loadData(userLocation?.lat, userLocation?.lng)
  }, [loadData, userLocation])

  const updateVisibleItems = useCallback((map: google.maps.Map) => {
    const bounds = map.getBounds()
    if (!bounds) return
    const itemsInBounds = allItems.filter((item) => {
      const position = new google.maps.LatLng(item.location.lat, item.location.lng)
      return bounds.contains(position)
    })
    setVisibleItems(itemsInBounds.length > 0 ? itemsInBounds : [])
  }, [allItems])

  const handleMapIdle = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const center = map.getCenter()
    if (center) {
      setMapCenter({ lat: center.lat(), lng: center.lng() })
    }
    setMapZoom(map.getZoom() || defaultZoom)
    updateVisibleItems(map)
  }, [updateVisibleItems])

  useEffect(() => {
    if (mapRef.current && allItems.length > 0) {
      updateVisibleItems(mapRef.current)
    }
  }, [allItems, updateVisibleItems])

  // Manage advanced markers
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isLoaded) return

    const updateMarkers = async () => {
      const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary
      const markers = markersRef.current

      // Remove markers that are no longer in visible items
      const visibleIds = new Set(visibleItems.map(item => item.id))
      for (const [id, marker] of markers.entries()) {
        if (!visibleIds.has(id)) {
          marker.map = null
          markers.delete(id)
        }
      }

      // Add or update markers for visible items
      for (const item of visibleItems) {
        let marker = markers.get(item.id)
        
        if (!marker) {
          // Create pin with custom color and label based on type
          const pinConfig = {
            trip: { background: '#3B82F6', glyph: 'T' },
            event: { background: '#F97316', glyph: 'E' },
            post: { background: '#10B981', glyph: 'P' },
          }[item.type]

          const pin = new PinElement({
            background: pinConfig.background,
            borderColor: '#ffffff',
            glyphColor: '#ffffff',
            glyph: pinConfig.glyph,
            scale: selectedItem?.id === item.id ? 1.25 : 1,
          })

          marker = new AdvancedMarkerElement({
            map,
            position: item.location,
            content: pin.element,
            title: item.title,
          })

          marker.addListener('click', () => {
            setSelectedItem(item)
          })

          markers.set(item.id, marker)
        } else {
          // Update existing marker scale if selection changed
          const pinConfig = {
            trip: { background: '#3B82F6', glyph: 'T' },
            event: { background: '#F97316', glyph: 'E' },
            post: { background: '#10B981', glyph: 'P' },
          }[item.type]

          const pin = new PinElement({
            background: pinConfig.background,
            borderColor: '#ffffff',
            glyphColor: '#ffffff',
            glyph: pinConfig.glyph,
            scale: selectedItem?.id === item.id ? 1.25 : 1,
          })
          marker.content = pin.element
        }
      }
    }

    updateMarkers()
  }, [visibleItems, selectedItem, isLoaded])

  const handleSearchPlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace()
    if (!place || !place.geometry?.location) return
    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()
    setSearchValue(place.formatted_address || place.name || '')
    setMapCenter({ lat, lng })
    setMapZoom(12)
    const map = mapRef.current
    if (map) {
      map.panTo({ lat, lng })
      map.setZoom(12)
      updateVisibleItems(map)
    }
  }, [updateVisibleItems])

  const handleUseMyLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return
    setGeolocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setUserLocation({ lat: latitude, lng: longitude })
        setMapCenter({ lat: latitude, lng: longitude })
        setMapZoom(12)
        const map = mapRef.current
        if (map) {
          map.panTo({ lat: latitude, lng: longitude })
          map.setZoom(12)
          updateVisibleItems(map)
        }
        setGeolocating(false)
      },
      () => setGeolocating(false),
      {
        enableHighAccuracy: true,
        maximumAge: 1000 * 60 * 5,
      },
    )
  }, [updateVisibleItems])

  const handleItemSelect = (item: ExploreItem) => {
    setSelectedItem(item)
    const map = mapRef.current
    if (map) {
      map.panTo(item.location)
      map.setZoom(13)
    }
  }

  useEffect(() => {
    if (selectedItem && !visibleItems.some((item) => item.id === selectedItem.id)) {
      setSelectedItem(null)
    }
  }, [visibleItems, selectedItem])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Compass className="h-6 w-6 text-gray-500" />
          Explore
        </h1>
        <p className="text-gray-600 mt-1">
          Discover trips, events, and posts by searching or moving around the map. Smart recommendations based on your location.
        </p>
      </div>

      <div className="card">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
              {isLoaded ? (
                <Autocomplete
                  onLoad={(autocomplete) => {
                    autocompleteRef.current = autocomplete
                  }}
                  onPlaceChanged={handleSearchPlaceChanged}
                  fields={['geometry', 'formatted_address', 'name']}
                >
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search for a place or address"
                    className="input w-full pl-10"
                  />
                </Autocomplete>
              ) : (
                <div className="input w-full pl-10 flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading map…
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <select
                value={contentFilter}
                onChange={(e) => setContentFilter(e.target.value as ContentFilter)}
                className="input"
              >
                <option value="all">All Content</option>
                <option value="trip">Trips Only</option>
                <option value="event">Events Only</option>
                <option value="post">Posts Only</option>
              </select>

              <button
                type="button"
                onClick={handleUseMyLocation}
                className="btn-secondary inline-flex items-center gap-2"
                disabled={geolocating}
              >
                {geolocating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                {geolocating ? 'Locating…' : 'Use my location'}
              </button>
            </div>
          </div>

          {loadError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Failed to load Google Maps. Please verify your API key configuration.
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="relative min-h-[420px] border border-gray-200 rounded-lg overflow-hidden">
              {isLoaded ? (
                <GoogleMap
                  center={mapCenter}
                  zoom={mapZoom}
                  options={mapOptions}
                  onLoad={(map) => {
                    mapRef.current = map
                    handleMapIdle()
                  }}
                  onIdle={handleMapIdle}
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                >
                  {/* Markers are now managed via useEffect hook above */}

                  {selectedItem && (
                    <InfoWindowF
                      position={selectedItem.location}
                      onCloseClick={() => setSelectedItem(null)}
                    >
                      <div className="max-w-xs space-y-2">
                        <div className="flex items-center gap-2">
                          <span className={`badge ${
                            selectedItem.type === 'trip' ? 'badge-info' : 
                            selectedItem.type === 'event' ? 'badge-warning' : 
                            'badge-success'
                          }`}>
                            {selectedItem.type.toUpperCase()}
                          </span>
                          <span className="font-semibold text-gray-900 text-sm">{selectedItem.title}</span>
                        </div>
                        
                        {selectedItem.subtitle && (
                          <p className="text-xs text-gray-600">
                            By {selectedItem.subtitle}
                          </p>
                        )}
                        
                        {selectedItem.location.address && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {selectedItem.location.address}
                          </p>
                        )}
                        
                        {selectedItem.coverImage && (
                          <img 
                            src={selectedItem.coverImage} 
                            alt={selectedItem.title}
                            className="w-full h-32 object-cover rounded"
                          />
                        )}
                        
                        {selectedItem.metadata?.status && (
                          <p className="text-xs text-gray-500">
                            Status: {selectedItem.metadata.status}
                          </p>
                        )}
                        
                        {(selectedItem.metadata?.startDate || selectedItem.metadata?.endDate) && (
                          <p className="text-xs text-gray-500">
                            {formatDate(selectedItem.metadata?.startDate)}
                            {selectedItem.metadata?.endDate ? ` – ${formatDate(selectedItem.metadata?.endDate)}` : ''}
                          </p>
                        )}
                        
                        {selectedItem.metadata?.distance !== undefined && (
                          <p className="text-xs text-gray-500">
                            Distance: {selectedItem.metadata.distance.toFixed(1)} km
                          </p>
                        )}
                        
                        {selectedItem.metadata?.stats && (
                          <div className="flex gap-3 text-xs text-gray-500">
                            {selectedItem.metadata.stats.likes !== undefined && (
                              <span>❤️ {selectedItem.metadata.stats.likes}</span>
                            )}
                            {selectedItem.metadata.stats.comments !== undefined && (
                              <span>💬 {selectedItem.metadata.stats.comments}</span>
                            )}
                            {selectedItem.metadata.stats.participantCount !== undefined && (
                              <span>👥 {selectedItem.metadata.stats.participantCount}</span>
                            )}
                          </div>
                        )}
                        
                        {selectedItem.description && (
                          <p className="text-xs text-gray-500 line-clamp-3">{selectedItem.description}</p>
                        )}
                      </div>
                    </InfoWindowF>
                  )}
                </GoogleMap>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm bg-gray-100">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading map…
                </div>
              )}
            </div>

            <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  Nearby items
                </h2>
                <span className="text-xs text-gray-500">
                  {visibleItems.length > 0 ? `${visibleItems.length} shown` : `${allItems.length} total`}
                </span>
              </div>

              {loading ? (
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading content…
                </div>
              ) : allItems.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No content with location data available.
                </div>
              ) : visibleItems.length === 0 ? (
                <div className="text-sm text-gray-500">
                  Move the map to discover content in other areas.
                </div>
              ) : (
                visibleItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleItemSelect(item)}
                    className={`w-full text-left border rounded-lg p-3 transition-colors ${selectedItem?.id === item.id ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/40'}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900 text-sm truncate">{item.title}</span>
                      <span className={`badge text-xs ${
                        item.type === 'trip' ? 'badge-info' : 
                        item.type === 'event' ? 'badge-warning' : 
                        'badge-success'
                      }`}>
                        {item.type}
                      </span>
                    </div>
                    {item.subtitle && (
                      <p className="text-xs text-gray-500 mb-1">
                        By {item.subtitle}
                      </p>
                    )}
                    {item.location.address && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                        <MapPin className="h-3 w-3" />
                        {item.location.address}
                      </p>
                    )}
                    {item.metadata?.distance !== undefined && (
                      <p className="text-xs text-gray-500">
                        {item.metadata.distance.toFixed(1)} km away
                      </p>
                    )}
                    {item.metadata?.stats && (
                      <div className="flex gap-2 text-xs text-gray-500 mt-1">
                        {item.metadata.stats.likes !== undefined && (
                          <span>❤️ {item.metadata.stats.likes}</span>
                        )}
                        {item.metadata.stats.comments !== undefined && (
                          <span>💬 {item.metadata.stats.comments}</span>
                        )}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
